var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef } from 'react';
import { Animated, findNodeHandle, Keyboard, Platform, ScrollView, StyleSheet, TextInput, UIManager, View } from 'react-native';
import { focusedRef } from '../Inputs/InputView';
import { useAnimation } from '../Animations/AnimationHooks';
export const AnimatedScrollView = (_a) => {
    var { children, onTouchStart, onTouchEnd, scrollViewRef, containerStyle, shadowStyle, noShadow, extraHeight = 0, extraScroll = 0 } = _a, otherProps = __rest(_a, ["children", "onTouchStart", "onTouchEnd", "scrollViewRef", "containerStyle", "shadowStyle", "noShadow", "extraHeight", "extraScroll"]);
    const ref = useRef();
    const scrollState = useRef(false);
    const scrollViewHeight = useRef(0);
    const scrollViewTop = useRef(0);
    const touchStartY = useRef(0);
    const touchEndY = useRef(0);
    const [animation, setAnimation] = useAnimation({
        initialValue: 0,
        duration: 50,
        nativeDriver: Platform.OS !== 'web'
    });
    const [heightAnimation, setHeightAnimation] = useAnimation({
        initialValue: 0,
        duration: 100,
        nativeDriver: false
    });
    const handleTouchStart = (event) => {
        onTouchStart === null || onTouchStart === void 0 ? void 0 : onTouchStart(event);
        touchStartY.current = event.nativeEvent.locationY;
    };
    const handleTouchEnd = (event) => {
        var _a;
        onTouchEnd === null || onTouchEnd === void 0 ? void 0 : onTouchEnd(event);
        touchEndY.current = event.nativeEvent.locationY;
        if (Math.abs(touchEndY.current - touchStartY.current) < 5) {
            (_a = TextInput.State.currentlyFocusedInput()) === null || _a === void 0 ? void 0 : _a.blur();
        }
    };
    const handleScrollViewRef = useCallback((new_ref) => {
        ref.current = new_ref;
        scrollViewRef === null || scrollViewRef === void 0 ? void 0 : scrollViewRef(new_ref);
    }, [scrollViewRef]);
    const handleScroll = useCallback((e) => {
        const isScrolled = e.nativeEvent.contentOffset.y > 5;
        if (Platform.OS !== 'web' && scrollState.current !== isScrolled) {
            setAnimation(isScrolled ? 1 : 0);
            scrollState.current = isScrolled;
        }
    }, []);
    const handleLayout = useCallback((e) => {
        scrollViewTop.current = e.nativeEvent.layout.top || e.nativeEvent.layout.x || 0;
    }, []);
    useEffect(() => {
        if (Platform.OS === 'web') {
            return;
        }
        const keyboardShow = Platform.select({
            default: 'keyboardWillShow',
            android: 'keyboardDidShow'
        });
        const keyboardHide = Platform.select({
            default: 'keyboardWillHide',
            android: 'keyboardDidHide'
        });
        let prevNode = undefined;
        const showHandler = Keyboard.addListener(keyboardShow, (frames) => {
            if (!ref.current) {
                return;
            }
            const node = TextInput.State.currentlyFocusedInput();
            if (!node) {
                return;
            }
            if (prevNode === node) {
                return;
            }
            prevNode = node;
            const currentlyFocusedField = findNodeHandle(node);
            if (!currentlyFocusedField) {
                return;
            }
            UIManager.viewIsDescendantOf(currentlyFocusedField, ref.current.getInnerViewNode(), (isAncestor) => {
                var _a;
                if (!isAncestor) {
                    return;
                }
                const responder = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.getScrollResponder();
                const additionalScroll = typeof extraScroll === 'number'
                    ? extraScroll
                    : extraScroll(focusedRef);
                let additionalHeight = typeof extraHeight === 'number'
                    ? extraHeight
                    : extraHeight(focusedRef);
                const offset = 70 + additionalScroll;
                additionalHeight += scrollViewTop.current;
                const position = Platform.select({
                    default: frames.endCoordinates.height / 1.5,
                    android: 0
                });
                setHeightAnimation(position + additionalHeight)
                    .then(() => {
                    if (Platform.OS === 'ios') {
                        responder === null || responder === void 0 ? void 0 : responder.scrollResponderScrollNativeHandleToKeyboard(currentlyFocusedField, offset, true);
                    }
                });
            });
        });
        const hideHandler = Keyboard.addListener(keyboardHide, () => {
            setHeightAnimation(0);
            prevNode = undefined;
        });
        return () => {
            showHandler.remove();
            hideHandler.remove();
        };
    }, [extraScroll, extraHeight]);
    return (_jsxs(View, { style: [styles.container, containerStyle], children: [_jsxs(ScrollView, Object.assign({ ref: handleScrollViewRef, showsVerticalScrollIndicator: false, keyboardShouldPersistTaps: 'always', scrollEventThrottle: 2, onScroll: handleScroll, onLayout: handleLayout, onContentSizeChange: (w, h) => scrollViewHeight.current = h, nestedScrollEnabled: true, onTouchStart: handleTouchStart, onTouchEnd: handleTouchEnd }, otherProps, { children: [children, _jsx(Animated.View, { style: { height: heightAnimation } })] })), Platform.OS !== 'web' && !noShadow && _jsx(Animated.View, { style: [styles.header, shadowStyle, { opacity: animation }] })] }));
};
const styles = StyleSheet.create({
    container: {
        overflow: 'hidden'
    },
    header: Object.assign({ position: 'absolute', top: -3, left: 0, right: 0, zIndex: 1000, paddingTop: 3, height: 0, backgroundColor: '#fff', shadowColor: '#000', shadowOpacity: 0.2, shadowOffset: {
            width: 0,
            height: 1
        } }, Platform.select({
        ios: {
            shadowRadius: 2
        },
        android: {
            elevation: 4
        }
    }))
});
